import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Mic, Square, X, Send } from "lucide-react";
import { useVoice } from "@/context/VoiceContext";
import { HELP_COMMANDS } from "@/lib/commands";
import { Spinner } from "@/components/primitives";

export default function MicButton() {
  const { supported, micState, caption, feedback, toggleListening, chatOpen, helpOpen, setHelpOpen, runText } = useVoice();
  const [text, setText] = useState("");

  const listening = micState === "listening";
  const processing = micState === "processing";
  const error = micState === "error";

  const ring = error
    ? "border-amber-500/70 text-amber-400 shadow-[0_0_24px_-6px_rgba(245,158,11,0.5)]"
    : listening
      ? "border-oxblood text-cream bg-oxblood shadow-[0_0_30px_-4px_rgba(110,30,40,0.9)]"
      : "border-oxblood/70 text-cream shadow-[0_0_20px_-6px_rgba(110,30,40,0.8)]";

  const submitText = (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    runText(text);
    setText("");
  };

  return (
    <>
      <div
        data-testid="mic-dock"
        className={`fixed z-[45] flex flex-col items-end gap-3 transition-[bottom,right] duration-500 ${
          chatOpen ? "bottom-28 right-4 lg:bottom-6 lg:right-[calc(min(440px,100vw)+1.5rem)]" : "bottom-6 right-4 sm:right-6"
        }`}
      >
        <AnimatePresence>
          {feedback && (
            <motion.div
              key="fb"
              data-testid="voice-feedback"
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
              className={`glass max-w-[280px] rounded-2xl px-4 py-2.5 text-xs leading-relaxed sm:max-w-xs ${feedback.tone === "warn" ? "text-amber-200" : "text-cream"}`}
            >
              {feedback.text}
            </motion.div>
          )}
          {listening && (
            <motion.div
              key="cap"
              data-testid="voice-caption"
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
              className="glass flex max-w-[280px] items-center gap-2 rounded-full px-4 py-2 text-xs text-cream sm:max-w-xs"
            >
              <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-oxblood" />
              <span className="truncate">{caption || "Listening…"}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {supported ? (
          <div className="group relative">
            <span className="pointer-events-none absolute -top-9 right-0 whitespace-nowrap rounded-full bg-panel px-3 py-1 font-mono text-[0.62rem] tracking-wider text-gray2 opacity-0 transition-opacity group-hover:opacity-100">
              Tap and speak
            </span>
            <div className="relative">
              {listening && (
                <>
                  <span className="sonar" />
                  <span className="sonar" style={{ animationDelay: "0.6s" }} />
                  <span className="sonar" style={{ animationDelay: "1.2s" }} />
                </>
              )}
              <button
                data-testid="mic-button"
                onClick={toggleListening}
                aria-label={listening ? "Stop listening" : "Tap and speak"}
                aria-pressed={listening}
                className={`relative grid h-14 w-14 place-items-center rounded-full border bg-noir/90 backdrop-blur transition-[transform,background-color,border-color,box-shadow] duration-300 hover:scale-105 ${ring}`}
              >
                {processing ? <Spinner className="h-5 w-5" /> : listening ? <Square className="h-4 w-4 fill-current" /> : <Mic className="h-5 w-5" />}
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submitText} data-testid="text-command-form" className="glass flex items-center gap-2 rounded-full py-1 pl-4 pr-1">
            <input
              data-testid="text-command-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type a command, e.g. show cases"
              className="w-44 bg-transparent text-xs text-cream outline-none placeholder:text-gray2/70 sm:w-56"
            />
            <button type="submit" aria-label="Run command" className="grid h-9 w-9 place-items-center rounded-full bg-oxblood text-cream">
              <Send className="h-4 w-4" />
            </button>
          </form>
        )}
      </div>

      <AnimatePresence>
        {helpOpen && (
          <motion.div
            data-testid="help-sheet"
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }}
            className={`glass fixed z-[46] w-[min(92vw,360px)] rounded-2xl p-5 ${chatOpen ? "bottom-48 right-4 lg:bottom-24 lg:right-[calc(min(440px,100vw)+1.5rem)]" : "bottom-24 right-4 sm:right-6"}`}
            role="dialog" aria-label="Voice commands"
          >
            <div className="mb-3 flex items-center justify-between">
              <span className="label">What you can say</span>
              <button data-testid="help-close" onClick={() => setHelpOpen(false)} aria-label="Close" className="text-gray2 hover:text-cream"><X className="h-4 w-4" /></button>
            </div>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5">
              {HELP_COMMANDS.map((c) => (
                <li key={c} className="text-xs text-cream/85">“{c}”</li>
              ))}
            </ul>
            <p className="mt-3 text-[0.68rem] text-gray2">Also works in Hindi and Tamil.</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
